<template>
<div data-component="FilterCreate.vue" class="a-loaded" :class="{ 'opacity-10 pointer-events-none': isLoading }">
<PageHeader :Back="true" :BackOnDesktop="true" :EmitInstead="true" :Title="'Dodaj usługę'" :HideBg="ScreenSize > 1" @back-click="backClick"/>
	<div v-if="ScreenSize <= 1" class="relative h-1 bg-neutral-300 -top-1 z-48">
		<div
			class="top-0 left-0 h-1 transition-all p-absolute bg-accent"
			:style="{ width: progressBarDone }"
		/>
	</div>
	<div v-if="!pricingCategoryId" :class="(!editLocation && !multi) ? 'bg-white p-6 lg:rounded-lg shadow flex flex-col gap-6 mt-3 lg:mx-5 lg:mt-6' : 'lg:py-2 px-5'">
		<div v-if="!editLocation && !multi" class="min-h-[12rem]">
			<!-- Wyszukiwarka -->
			<div class="relative form-input">
				<Autocomplete
					v-model="newCategoryAutocomplete.Value"
					class="filter-create-autocomplete | [&>input]:pl-10"
					:Autocomplete="newCategoryAutocomplete.Autocomplete"
					:Suggestions="newCategoryAutocomplete.Suggestions"
					Placeholder="Zacznij pisać nazwę usługi"
					:IgnoreValues="[ 1, 999999 ]"
					:Autoselect="false"
					@select="autocompleteSelect"
					@process="autocompleteProcess"
				/>
				<BaseIcon name="search" size="1.25" class="absolute pointer-events-none left-2 top-4 text-neutral-300"/>
			</div>
			<div class="pt-6">
				Pasujące do Ciebie:
				<div class="flex flex-wrap gap-2 my-4">
					<div
						v-for="item in searchList"
						:key="item.cid"
						class="px-3 py-1 text-sm border rounded-full cursor-pointer"
						:class="item.cid === selectedCategories[0]?.cid ? 'bg-info text-white border-info' : 'border-neutral-300'"
						@click="selectCategory(item)"
						v-html="item.name + (item.service ? '' : ' <small>Sprzedaż</small>')"
					/>
				</div>
			</div>
		</div>

		<div v-else-if="!editLocation">
			<!-- Wyszukiwarka -->
			<div class="relative my-5 form-input">
				<input ref="search" v-model="searchQuery" type="search" class="pl-10" placeholder="Znajdź swoją usługę" autofocus @keyup="onSearch" @change="onSearch" @search="onSearch">
				<BaseIcon name="search" size="1.25" class="absolute pointer-events-none left-2 top-4 text-neutral-300"/>
				<div v-if="displayValidation" class="mt-1 text-xs text-danger">
					Zacznij pisać nazwę usługi i wybierz jedną z podpowiedzi
				</div>
			</div>

			<div
				class="flex flex-col"
				:style="{ 'max-height': `calc(100vh - ${ScreenSize > 1 ? 21 : 16}rem)` }"
			>
				<!-- Wyszukane kategorie -->
				<div class="overflow-auto">
					<div
						v-if="(!searchList.length && searchQuery)"
						class="my-3 font-bold"
						v-html="`Przykro nam, nie znaleźliśmy usługi o nazwie ${searchQuery}.`"
					/>
					<div
						v-else
						class="sticky top-0 pt-3 text-xs font-bold bg-white text-neutral-700"
						v-html="searchQuery ? 'Znaleziono dla Ciebie:' : 'Proponowane dla Ciebie:'"
					/>

					<div
						v-for="item in searchList"
						:key="item.cid"
						class="pl-3 my-3 cursor-pointer hover:text-accent"
						:class="{
							'text-accent': selectedCategories.find((i) => i.cid === item.cid),
							'hidden': item.cid === 999999,
						}"
						@click="selectCategory(item)"
						v-html="`${item.name} ${!item.service ? '<small>Sprzedaż</small>' : ''}`"
					/>
				</div>

				<!-- Wybrane kategorie -->
				<div v-if="(multi && selectedCategories.length)" class="overflow-auto border-t border-neutral-100 max-h-32">
					<div class="sticky top-0 pt-3 text-xs font-bold bg-white text-neutral-700" v-html="'Wybrane usługi:'"/>
					<div
						v-for="item in selectedCategories"
						:key="item.cid"
						class="pl-3 my-3 cursor-pointer hover:text-accent"
						@click="selectCategory(item)"
						v-html="`${item.name} ${!item.service ? '<small>Sprzedaż</small>' : ''}`"
					/>
				</div>
			</div>
		</div>

		<div v-else-if="editLocation">
			<!-- Edycja lokalizacji -->
			<FilterDetailsLocation
				v-if="filter"
				:servicesObject="servicesObject"
				:filter="filter"
			/>
		</div>

		<!-- Dolny pasek z przyciskami -->
		<div class="h-24 lg:hidden"/>
		<div class="fixed inset-x-0 bottom-0 flex justify-end p-3 bg-white lg:sticky shadow-default z-2 lg:bg-white/50 lg:shadow-white lg:w-max lg:ml-auto">
			<button
				class="w-full btn btn-primary lg:w-auto"
				:class="{ 'bg-neutral-300 hover:bg-neutral-300': !selectedCategories.length}"
				@click="onCreateButton"
				v-html="'Dodaj usługę'"
			/>
		</div>
	</div>
	<div v-else-if="pricingCategoryId">
		<!-- Edycja cen -->
		<FilterPricing
			ref="FilterPricing"
			:servicesObject="servicesObject"
			:apiNamespace="apiNamespace"
			:categoryId="pricingCategoryId"
			@save-button-clicked="createFilter"
			@send-analytics-event="$emit('send-analytics-event', $event)"
		/>
	</div>
</div>
</template>

<script lang="ts">
import { mapState, mapWritableState } from 'pinia';
import { useServicesStore } from '../../../stores/services';
import Filter from '../../../types/interfaces/services/Filter';
import { Services } from '../../../types/interfaces/services/Services';
import FilterDetailsLocation from './FilterDetails/FilterDetailsLocation.vue';
import FilterPricing from './FilterPricing.vue';
import serverFetch from '../../../globals/ServerFetch';
import Autocomplete from '../../../globals/components/base/Autocomplete.vue';

/**
 * Element listy kategorii zwracany przez `get-suggested-categories.ofer`
 */
interface CategorySuggestion {
	/** Identyfikator kategorii. */
	cid:number;
	/** Czy jest to kategoria usługowa? */
	service:boolean;
	/** Nazwa kategorii. */
	name:string;
}

/**
 * Ekran dodawania nowych kategorii na Usługach.
 */
export default {
	name: 'FilterCreate',
	components: {
		FilterDetailsLocation,
		FilterPricing,
	},
	props: {
		/**
		 * Główny obiekt usług
		 */
		servicesObject: {
			type: Object as () => Services,
			required: true,
		},

		/**
		 * Czy można wybrać kilka kategorii?
		 */
		multi: {
			type: Boolean,
			default: false,
		},

		/**
		 * Początek ścieżki do API.
		 */
		apiNamespace: {
			type: String,
			required: true,
		},

		/**
		 * Filtr na którym będą edytowane lokalizacje.
		 */
		filter: {
			type: Object as () => Filter,
			required: false,
		},
	},
	emits: ['get-services-list', 'go-back', 'toggle-loading', 'go-to-add-group-section', 'send-analytics-event', 'apply-multiple-groups-changes', 'save-multiple-groups', 'filter-create', 'filter-create-template'],
	data() {
		return {
			/**
			 * Wyszukiwana fraza
			 */
			searchQuery: '',

			/**
			 * Lista wyszukanych lub polecanych kategorii.
			 */
			searchList: [] as CategorySuggestion[],

			/**
			 * Lista wybranych kategorii do dodania.
			 */
			selectedCategories: [] as CategorySuggestion[],

			/**
			 * Czy wyświetlić komunikat walidacyjny przy próbie
			 * utworzenia filtru bez jej wybrania?
			 */
			displayValidation: false,

			/**
			 * Czy filtr jest aktualnie tworzony?
			 */
			isLoading: false,

			/**
			 * Czy znajdujemy się na ekranie wyboru lokalizacji
			 * w przypadku dodawania wielu filtrów na raz?
			 */
			editLocation: false,
		};
	},
	computed: {
		newCategoryAutocomplete() {
			const url = `/${this.apiNamespace}/get-suggested-categories.ofer?addNewCategoryUserInput={{q}}&idCustomer=${this.customerId}`;
			return {
				Value: ['', ''],
				Autocomplete: url,
				Suggestions: [],
			};
		},
		...mapState(useServicesStore, [
			'customerId',
		]),
		...mapWritableState(useServicesStore, [
			'currentPage',
			'pricingCategoryId',
			'isNewCategory',
		]),

		/**
		 * Wybrane lokalizacje
		 */
		selectedLocations() {
			if (!this.filter) { return null; }
			return this.filter.location?.selected;
		},

		/**
		 * Zwraca w % w jakim stopniu ma zostać wypełniony pasek postępu
		*/
		progressBarDone() {
			if (this.pricingCategoryId || this.editLocation) {
				return '100%';
			}

			return this.selectedCategories.length ? '50%' : '0';
		},
	},
	mounted() {
		this.$emit('send-analytics-event', 'Add_Category_Show', 'addCategory_show');
		this.onSearch();
	},
	methods: {
		autocompleteSelect(value : { Value:number, Input:string }) {
			this.selectedCategories = [{
				cid: value.Value,
				name: value.Input,
				service: false,
			}];
		},

		autocompleteProcess(list:string, component:InstanceType<typeof Autocomplete>) {
			this.selectedCategories = [];
			if (!list.length) {
				component.InputSuggestions = [];
				return;
			}

			const { suggestedCategories } = JSON.parse(list);

			component.InputSuggestions = suggestedCategories.map(({ cid, name } : { cid:number, name:string }) => ({
				Value: cid,
				Label: name,
				Input: name,
			})).filter((i) => i.Value !== 1 && i.Value !== 999999);
		},

		/**
		 * Tworzy filtr lub kilka filtrów z wybranymi lokalizacjami z ostatniego filtra.
		 */
		createFilter() {
			this.isLoading = true;
			this.$emit('filter-create', {
				categoryIds: this.selectedCategories.map((i) => i.cid),
				location: this.selectedLocations,
			});
		},

		/**
		 * Akcja po kliknięciu przycisku dodaj.
		 * Tworzy nowy filtr lub (jeżeli parametr `multi`) jest aktywny - kilka filtrów
		 * oraz przenosi użytkownika na ekran wyboru ceny.
		 * W przypadku kilku filtrów, po kliknięciu wyświetla ekran wyboru lokalizacji.
		 * Jeśli żaden z fitlrów nie został wybrany, pokaż komunikat walidacyjny.
		 */
		onCreateButton() {
			const { length } = this.selectedCategories;
			if (length === 0) { this.displayValidation = true; }
			if (length === 1) {
				this.pricingCategoryId = this.selectedCategories[0].cid;
			}
			if (length > 1) {
				this.$emit('filter-create-template', 1, [], true);
				this.editLocation = true;
			}
			if (this.editLocation) { this.createFilter(); }
		},

		/**
		 * Zaznacza lub odznacza wybraną kategorię z listy.
		 * Jeśli parametr `multi` nie jest aktywny, zamiast tego zastąp wybór danym elementem.
		 * @param {CategorySuggestion} category element na liście
		 */
		async selectCategory(category:CategorySuggestion) {
			if (!this.multi) {
				this.selectedCategories = [category];
				this.searchQuery = category.name;
				this.newCategoryAutocomplete.Value = ['!', category.name];
			} else {
				const index = this.selectedCategories.findIndex((i) => i.cid === category.cid);
				if (index === -1) {
					this.selectedCategories.push(category);
				} else {
					this.selectedCategories.splice(index, 1);
				}
			}
		},

		/**
		 * Wyszukuje kategorie wg. podanej `searchQuery` lub jeżeli jest pusta - listę sugerowanych kategorii.
		 */
		async onSearch() {
			this.displayValidation = false;
			if (!this.multi) { this.selectedCategories = []; }
			const url = `/${this.apiNamespace}/get-suggested-categories.ofer?addNewCategoryUserInput=${this.searchQuery}&idCustomer=${this.customerId}`;
			const rsvp = await serverFetch(url) as { suggestedCategories:CategorySuggestion[] };
			if (rsvp.suggestedCategories) {
				this.searchList = rsvp.suggestedCategories.splice(0, this.multi ? 20 : 5);
			}
		},
		backClick() {
			this.isNewCategory = false;
			this.currentPage = '';
			this.$router.back();
		},
	},
};
</script>
